import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ApiService, HttpMethod } from '../api/api.service';
import { AuthResponse, RefreshResponse } from 'src/app/modules/auth/models/auth.model';
import { TokenService } from '../token/token.service';
import { AuthEventsService, AuthEventType } from './auth-events.service';
import { UserRoleService } from '../user-role/user-role.service';
// GetMeResponse import is no longer needed here as caching is moved
import { CacheService } from '../cache/cache.service'; // Import CacheService

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private authStatusSubject = new BehaviorSubject<boolean>(false);
  public authStatus$: Observable<boolean> = this.authStatusSubject.asObservable();
  private subscriptions: Subscription[] = [];

  constructor(
    private apiService: ApiService,
    private tokenService: TokenService,
    private authEventsService: AuthEventsService,
    private userRoleService: UserRoleService,
    private cacheService: CacheService // Inject CacheService
  ) {
    this.initializeAuthStatus();
    this.listenToAuthEvents();
  }



  async login(username: string, password: string): Promise<boolean> {
    try {
      const body = { username, password };
      const response = await this.apiService.call<AuthResponse>(HttpMethod.POST, 'auth/login', body);
      this.tokenService.setToken(response.accessToken);
      this.tokenService.setRefreshToken(response.refreshToken);
      
      // Emit login event
      this.authEventsService.emitEvent({
        type: AuthEventType.LOGGED_IN
      });
      
      // Load user role information
      this.userRoleService.loadCurrentUser();
      
      this.authStatusSubject.next(true);
      return true;
    } catch (login_error) {
      console.error('Login error:', login_error);
      return false;
    } 
  }

  async logout(): Promise<void> {
    try {
      console.log('Logout called');
      
      try {
        // Try to call the logout endpoint, but don't wait for it to complete
        // This prevents any issues with the API call from blocking the logout process
        this.apiService.call<void>(HttpMethod.POST, 'auth/logout')
          .catch(error => console.warn('Logout API call failed, but continuing with local logout:', error));
      } catch (api_error) {
        // Ignore API errors during logout
        console.warn('Error during logout API call, but continuing with local logout:', api_error);
      }
      
      // Always clear local tokens and data regardless of API response
      this.tokenService.clearToken();
      this.tokenService.clearRefreshToken();
      
      // Clear user data from local storage
      localStorage.removeItem('userData'); // This seems to be a different item, keeping it.
      this.cacheService.clearCachedUserDetails(); // Use CacheService
      
      // Emit logout event
      this.authEventsService.emitEvent({
        type: AuthEventType.LOGGED_OUT
      });
      
      // Update auth status
      this.authStatusSubject.next(false);
      
      console.log('Logout completed, redirecting to login page');
      
      // Redirect to login page
      window.location.href = '/auth/login';
    } catch (logout_error) {
      console.error('Logout error:', logout_error);
      
      // Even if there's an error, try to redirect to login
      window.location.href = '/auth/login';
    }
  }

  async forgotPassword(email: string): Promise<boolean> {
    try {
      const body = { email };
      await this.apiService.call<void>(HttpMethod.POST, 'forgot', body);
      return true;
    } catch (forgot_error) {
      console.error('Forgot password error:', forgot_error);
      return false;
    }
  }

  async resetPassword(email: string, newPassword: string, verificationCode: string): Promise<boolean> {
    try {
      const body = { email, newPassword, verificationCode };
      await this.apiService.call<void>(HttpMethod.POST, 'reset', body);
      return true;
    } catch (reset_error) {
      console.error('Reset password error:', reset_error);
      return false;
    }
  }

  async refreshToken(): Promise<boolean> {
    try {
      const response = await this.apiService.call<AuthResponse>(HttpMethod.POST, 'auth/refresh');
      this.tokenService.setToken(response.accessToken);
      return true;
    } catch (refresh_error) {
      console.error('Refresh token error:', refresh_error);
      return false;
    }
  }

  private initializeAuthStatus(): void {
    const isValid = this.tokenService.isTokenValid();
    this.authStatusSubject.next(isValid);
    
    // If token is valid, load user role information
    if (isValid) {
      this.userRoleService.loadCurrentUser();
    }
  }

  private listenToAuthEvents(): void {
    this.subscriptions.push(
      this.authEventsService.authEvents$.subscribe(event => {
        console.log(`[AuthService] Received auth event: ${event.type}`);
        switch (event.type) {
          case AuthEventType.SESSION_EXPIRED:
            console.log('[AuthService] Session expired, updating auth status and redirecting to login');
            this.cacheService.clearCachedUserDetails(); // Use CacheService
            this.authStatusSubject.next(false);
            // Use router navigation instead of window.location for better SPA behavior
            // This prevents full page reloads which can cause the repetitive loading issue
            // window.location.href = '/auth/login';
            
            // Delay the redirect slightly to avoid potential race conditions
            setTimeout(() => {
              console.log('[AuthService] Redirecting to login page');
              window.location.href = '/auth/login';
            }, 100);
            break;
          case AuthEventType.LOGGED_IN:
            console.log('[AuthService] User logged in, updating auth status');
            this.authStatusSubject.next(true);
            break;
          case AuthEventType.LOGGED_OUT:
            console.log('[AuthService] User logged out, updating auth status');
            this.authStatusSubject.next(false);
            break;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  public expireAuthToken(): void {
    this.authStatusSubject.next(false);
  }
  /**
   * Checks if the user is authenticated by verifying the token's 
   * expiration date
   * @returns boolean
   */
  public isAuthenticated(): boolean {
    return this.tokenService.isTokenValid();
  }

  public getUserId(): string | null {
    return this.tokenService.getUserId();
  }

  /**
   * Checks if an email is valid for signup
   * @param email The email to check
   * @returns Promise with boolean indicating if email is valid for signup
   */
  async checkEmailForSignup(email: string): Promise<boolean> {
    try {
      const body = { email };
      const response = await this.apiService.callNoauth<{valid: boolean}>(HttpMethod.POST, 'auth/check-email', body);
      return response.valid;
    } catch (error) {
      console.error('Email check error:', error);
      return false;
    }
  }

  /**
   * Verifies the 6-digit code sent to the user's email
   * @param email The user's email
   * @param code The 6-digit verification code
   * @returns Promise with boolean indicating if code is valid
   */
  async verifySignupCode(email: string, code: string): Promise<boolean> {
    try {
      const body = { email, code };
      const response = await this.apiService.callNoauth<{valid: boolean}>(HttpMethod.POST, 'auth/verify-code', body);
      return response.valid;
    } catch (error) {
      console.error('Code verification error:', error);
      return false;
    }
  }

  /**
   * Resends the 6-digit verification code to the user's email
   * @param email The user's email
   * @returns Promise with boolean indicating if resend was successful
   */
  async resendSignupCode(email: string): Promise<boolean> {
    try {
      const body = { email };
      await this.apiService.callNoauth<void>(HttpMethod.POST, 'auth/resend-code', body);
      return true;
    } catch (error) {
      console.error('Resend code error:', error);
      return false;
    }
  }

  /**
   * Completes the signup process by creating a password
   * @param email The user's email
   * @param password The user's chosen password
   * @param code The verification code (optional if already verified)
   * @returns Promise with boolean indicating if signup was successful
   */

  async completeSignup(
    email: string,
    password: string,
    code?: string
  ): Promise<boolean> {                     // ← boolean result
    try {
      const body = { email, password, code };

      // Tell apiService what shape to expect from the backend
      const res = await this.apiService.callNoauth<{ success: boolean }>(
        HttpMethod.POST,
        'auth/register',
        body
      );

      return res.success;               
    } catch (err) {
      console.error('Signup completion error:', err);
      throw err;                        
    }
  }

  /**
   * Resend an invitation email to a user
   * @param email The invitee's email address
   * @returns Promise with boolean indicating if resend was successful
   */
  async resendInvitation(email: string): Promise<boolean> {
    try {
      const body = { email };
      await this.apiService.call<void>(HttpMethod.POST, 'auth/resend-invitation', body);
      return true;
    } catch (error) {
      console.error('Resend invitation error:', error);
      return false;
    }
  }

  /**
   * Initiates the password reset process by sending a reset link to the user's email
   * @param email The user's email
   * @returns Promise with boolean indicating if the reset link was sent successfully
   */
  async initiatePasswordReset(email: string): Promise<boolean> {
    try {
      const body = { email };
      await this.apiService.callNoauth<void>(HttpMethod.POST, 'auth/initiate-password-reset', body);
      return true;
    } catch (error) {
      console.error('Initiate password reset error:', error);
      return false;
    }
  }

  /**
   * Resets the password using a token
   * @param token The reset token
   * @param newPassword The new password
   * @returns Promise with boolean indicating if the password reset was successful
   */
  async resetPasswordWithToken(token: string, newPassword: string): Promise<boolean> {
    try {
      const body = { token, newPassword };
      await this.apiService.callNoauth<void>(HttpMethod.POST, 'auth/password-reset', body);
      return true;
    } catch (error) {
      console.error('Password reset error:', error);
      return false;
    }
  }

  /**
   * Decodes the token and retrieves its expiration time
   * @param token The JWT token
   * @returns The expiration date of the token, or null if invalid
   */
  getTokenExpiration(token: string): Date | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload && payload.exp) {
        return new Date(payload.exp * 1000);
      }
      return null;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }
}
